<script setup>

import {onMounted, reactive, ref, watch} from "vue";

const props = defineProps({
    automationId: {
        type: Number,
        required: true,
    },
})

const automation = ref(null)
const deliveries = ref([])
const pagination = reactive({
    links: {},
    meta: {}
})
const page = ref(1)
const search = ref('')
const url = ref(route('api.bo.crm.automation.deliveries', props.automationId))

const loadAutomation = async () => {
    const res = await axios.get(route('api.bo.crm.automation.show', props.automationId))

    automation.value = res.data.automation
}

const loadDeliveries = async () => {
    const res = await axios.get(url.value, {params: {search: search.value}})
    pagination.links = res.data.links
    pagination.meta = res.data.meta
    deliveries.value = res.data.data
}

const goToPrevPage = () => {
    if (page.value === 1) {
        return;
    }
    url.value = pagination.links.prev;
    page.value--;
}

const goToNextPage = () => {
    if (page.value >= pagination.meta.last_page) {
        return;
    }
    url.value = pagination.links.next;
    page.value++;
}

watch(page, async (newPage) => {
    await loadDeliveries()
}, { deep:true});

onMounted(() => {
    loadAutomation()
    loadDeliveries()
})

</script>
<template>
    <div>
        <div v-if="automation">
            <div :class="automation.excluded_lead_tags.length > 0 ? 'lg:grid lg:grid-cols-2 lg:gap-8 space-y-4 lg:space-y-0' : ''">
                <div>
                    <input-label
                        label="Tags that run this automation:"
                        class="mb-1"
                    />
                    <ul class="flex space-x-1 flex-wrap">
                        <li
                            v-for="(tag, t) in automation.included_lead_tags"
                            class="mb-1"
                        >
                            <pill
                                class="border  bg-gray-200 border-gray-300 flex space-x-2 cursor-default"
                                size="sm"
                                :style="tag.color ? {backgroundColor: tag.color, color: tag.textcolor} : {}"
                            >
                                <component
                                    :is="tag.description ? 'container-with-tooltip' : 'span'"
                                >
                                    <i class="fa-regular fa-tag mr-0.5 opacity-50"></i>
                                    <span>{{ tag.name }}</span>
                                    <template
                                        v-if="tag.description"
                                        #tooltip
                                    >
                                        <div class="block w-24 text-xxs text-black">
                                            {{ tag.description }}
                                        </div>
                                    </template>
                                </component>
                            </pill>

                        </li>
                    </ul>
                </div>
                <div
                    v-if="automation.excluded_lead_tags.length > 0"
                >
                    <input-label
                        label="Tags that prevent this automation:"
                        class="mb-1"
                    />
                    <ul class="flex space-x-1 flex-wrap">
                        <li
                            v-for="(tag, t) in automation.excluded_lead_tags"
                            class="mb-1"
                        >
                            <pill
                                class="border  bg-gray-200 border-gray-300 flex space-x-2 cursor-default"
                                size="sm"
                                :style="tag.color ? {backgroundColor: tag.color, color: tag.textcolor} : {}"
                            >
                                <component
                                    :is="tag.description ? 'container-with-tooltip' : 'span'"
                                >
                                    <i class="fa-regular fa-tag mr-0.5 opacity-50"></i>
                                    <span>{{ tag.name }}</span>
                                    <template
                                        v-if="tag.description"
                                        #tooltip
                                    >
                                        <div class="block w-24 text-xxs text-black">
                                            {{ tag.description }}
                                        </div>
                                    </template>
                                </component>
                            </pill>

                        </li>
                    </ul>
                </div>
            </div>

            <p class="text-center text-gray-500 mt-8">
                {{ pagination.meta.total}} deliveries for <a :href="route('bo.crm.automations.edit', props.automationId)" class="mf-link">{{ automation.name }}</a>
            </p>

            <div
                class="flex items-center justify-end space-x-1 pt-1"
            >
                <input-text
                    v-model:content="search"
                    :placeholder="_mft('shared:search') + ' ' + _mft('shared:email').toLowerCase()"
                    :show-clear="true"
                    @content-cleared="loadDeliveries()"
                    @enter-pressed="loadDeliveries()"
                    class="w-40 !mb-0 -mt-1"
                    input-class="text-xs"
                />

                <button-default
                    size="sm"
                    @click="loadDeliveries"
                >
                    {{ _mft('shared:search') }}
                </button-default>
            </div>

            <list-basic>
                <list-basic-item v-for="delivery in deliveries" :key="delivery.id">
                    <template #status>
                        <div class="mr-2">
                            <i
                                class="fa-light"
                                :class="delivery.opened ? 'fa-envelope-open text-myflowGreen' : 'fa-envelope text-gray-400'"
                            ></i>
                        </div>
                    </template>
                    <div class="md:grid md:grid-cols-3 md:gap-4">
                        <div class="col-span-2">
                            <div>
                                <a
                                    :href="route('crm.leads.view', delivery.sent_to.lead_id)"
                                    class="mf-link"
                                >
                                    {{ delivery.sent_to.name }}
                                </a>
                            </div>
                            <div class="text-xs text-gray-500">{{ delivery.sent_to.email }}</div>
                        </div>
                        <div class="text-xs md:text-right text-gray-500 mt-2 md:mt-2 mb-4 md:mb-0">
                            {{ delivery.sent_at.user_tz_display }}
                        </div>
                    </div>
                </list-basic-item>
            </list-basic>

            <div
                v-if="pagination.meta.last_page > 1"
                class="flex justify-center items-center space-x-4 mt-4"
            >
                <button-base
                    :disabled="pagination.meta.current_page <= 1"
                    @click="goToPrevPage"
                    size="sm"
                >
                    Prev
                </button-base>
                <div class="min-w-8 text-center">{{ pagination.meta.current_page }} / {{ pagination.meta.last_page }}</div>
                <button-base
                    :disabled="pagination.meta.current_page >= pagination.meta.last_page"
                    @click="goToNextPage"
                    size="sm"
                >
                    Next
                </button-base>
            </div>
        </div>
    </div>
</template>
